import React from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

const ReportConfiguration = ({ config, onConfigChange, selectedTemplate }) => {
  const colorSchemes = [
    { id: 'default', name: 'Default', primary: '#6366f1', secondary: '#8b5cf6' },
    { id: 'professional', name: 'Professional', primary: '#0f172a', secondary: '#334155' },
    { id: 'vibrant', name: 'Vibrant', primary: '#ec4899', secondary: '#f59e0b' },
    { id: 'nature', name: 'Nature', primary: '#10b981', secondary: '#059669' }
  ];

  const handleSectionToggle = (section) => {
    onConfigChange({
      ...config,
      sections: {
        ...config?.sections,
        [section]: !config?.sections?.[section]
      }
    });
  };

  const handleBrandingChange = (field, value) => {
    onConfigChange({
      ...config,
      branding: {
        ...config?.branding,
        [field]: value
      }
    });
  };

  const handleLogoUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleBrandingChange('logo', reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="Settings" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Report Configuration
          </h3>
          <p className="text-sm text-muted-foreground font-caption">
            Customize branding and report sections
          </p>
        </div>
      </div>

      {/* Branding Section */}
      <div className="mb-6">
        <h4 className="text-sm font-heading font-semibold text-foreground mb-3">
          Branding
        </h4>
        <div className="space-y-4">
          <Input
            label="Company Name"
            type="text"
            value={config?.branding?.companyName}
            onChange={(e) => handleBrandingChange('companyName', e?.target?.value)}
            placeholder="Enter company name"
          />

          <div>
            <label className="block text-sm font-medium text-foreground font-body mb-2">
              Company Logo
            </label>
            <div className="flex items-center gap-3">
              {config?.branding?.logo ? (
                <div className="w-16 h-16 border border-border rounded-lg overflow-hidden">
                  <img
                    src={config?.branding?.logo}
                    alt="Company logo"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 border-2 border-dashed border-border rounded-lg flex items-center justify-center">
                  <Icon name="Image" size={24} color="var(--color-muted-foreground)" />
                </div>
              )}
              <label className="cursor-pointer px-4 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-muted transition-smooth">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                Upload Logo
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground font-body mb-2">
              Color Scheme
            </label>
            <div className="grid grid-cols-2 gap-2">
              {colorSchemes?.map((scheme) => (
                <button
                  key={scheme?.id}
                  onClick={() => handleBrandingChange('colorScheme', scheme?.id)}
                  className={`flex items-center gap-2 p-2 border rounded-md transition-smooth ${
                    config?.branding?.colorScheme === scheme?.id
                      ? 'border-primary bg-primary/10' :'border-border hover:bg-muted'
                  }`}
                >
                  <div className="flex gap-1">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: scheme?.primary }}
                    />
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: scheme?.secondary }}
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground font-body">
                    {scheme?.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Report Sections */}
      <div className="mb-6">
        <h4 className="text-sm font-heading font-semibold text-foreground mb-3">
          Report Sections
        </h4>
        <div className="space-y-2">
          {Object.entries(config?.sections)?.map(([key, value]) => (
            <label
              key={key}
              className="flex items-center justify-between p-3 border border-border rounded-md hover:bg-muted cursor-pointer transition-smooth"
            >
              <span className="text-sm text-foreground font-body capitalize">
                {key?.replace(/([A-Z])/g, ' $1')?.trim()}
              </span>
              <input
                type="checkbox"
                checked={value}
                onChange={() => handleSectionToggle(key)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary/50"
              />
            </label>
          ))}
        </div>
      </div>

      {/* Additional Options */}
      <div>
        <h4 className="text-sm font-heading font-semibold text-foreground mb-3">
          Additional Options
        </h4>
        <div className="space-y-2">
          <label className="flex items-center justify-between p-3 border border-border rounded-md hover:bg-muted cursor-pointer transition-smooth">
            <span className="text-sm text-foreground font-body">Include Charts & Graphs</span>
            <input
              type="checkbox"
              checked={config?.includeCharts}
              onChange={(e) => onConfigChange({ ...config, includeCharts: e?.target?.checked })}
              className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary/50"
            />
          </label>
          <label className="flex items-center justify-between p-3 border border-border rounded-md hover:bg-muted cursor-pointer transition-smooth">
            <span className="text-sm text-foreground font-body">Include Appendix</span>
            <input
              type="checkbox"
              checked={config?.includeAppendix}
              onChange={(e) => onConfigChange({ ...config, includeAppendix: e?.target?.checked })}
              className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary/50"
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default ReportConfiguration;